import { computed, Ref, ref } from 'vue';
import { checkboxField, inputField } from '@/ui/components/form';
import { Task, useTask, useValidation } from '@/ui/concerns';
import { Role, RoleRecord, validate } from '@/entities/role';
import { apiErrorMessage } from '@/ui/components';
import { t } from '@/ui/i18n/i18n';
import { SelectOptions, SortDirection } from '@/ui/types';
import { EVENTS, EventTypes, trackEvent } from '@/services/activityTracker/activityTrackerHelper';
import { list as getRoleList } from '@/services/api/resources/role';
import { toasted } from '@/ui/helpers';
import { permissionsDropdown } from '@/ui/components/permission/PermissionsDropdown';
import { FeatureNames, newFeature } from '@/ui/components/NewFeature';
import { CreateUserModelIdentifier } from '@/ui/components/user/CreateUserModal';
import { chooseUserFieldWithGroups } from '@/ui/components/role/ChooseUserFieldWithGroups';
import { useSsoConfigurationRecord } from '@/ui/pages/sso/useSsoConfigurationRecord';
import { get as getSso } from '@/services/api/resources/sso-configuration/actions';
import { hasSsoEnabled, user } from '@/ui/auth/auth';

const fetchSsoConfiguration = () => {
  const record = useSsoConfigurationRecord();

  useTask(
    () => getSso(user.value.organization.id),
    ({ data }) => {
      record.value = data.data;
    },
  ).run();

  return record;
};

export function useRoleForm(record: Ref<Role>, task: Task) {
  const ssoConfiguration = hasSsoEnabled.value ? fetchSsoConfiguration() : undefined;

  const hasDuplicatedName = (roles: RoleRecord[], roleBeingUpdated: RoleRecord | undefined) => {
    if (roleBeingUpdated) {
      return roles.some(
        role => role.data.title === record.value.title && roleBeingUpdated.id !== role.id,
      );
    }

    return roles.some(
      role => role.data.title === record.value.title,
    );
  };

  const isGettingRoleList = ref(false);

  const isLoading = computed(() => isGettingRoleList.value || task.state.value.pending);

  const {
    validationErrors,
    hasNoValidationErrors,
    showValidationErrors,
  } = useValidation(validate, record, task.state);

  const handleSubmit = async (roleBeingUpdated: RoleRecord | undefined) => {
    showValidationErrors.value = true;

    isGettingRoleList.value = true;

    useTask(
      () => getRoleList({
        page: 1,
        perPage: 5,
        search: record.value.title,
        sort: 'title',
        dir: SortDirection.Descending,
      }),
      response => {
        isGettingRoleList.value = false;

        if (!hasNoValidationErrors.value) {
          return;
        }

        if (hasDuplicatedName(response.data, roleBeingUpdated)) {
          toasted.error(t('role.role-title-exists'));

          return;
        }

        task.run();
      },
    ).run();
  };

  const onlyUnique = (data: unknown[]) => [...new Set(data)];

  const formFields = (
    userOptions: SelectOptions,
  ) => [
    inputField({
      name: 'title',
      label: t('role.title'),
      placeholder: t('role.enter-title'),
      value: record.value.title,
      required: true,
      errors: validationErrors.value,
      disabled: task.state.value.pending,
    },
    value => {
      record.value.title = value;
    }),

    permissionsDropdown<string>({
      name: 'permissions',
      label: t('common.permissions'),
      value: record.value.permissions,
      placeholder: t('role.to-permissions'),
      required: true,
      errors: validationErrors.value,
      disabled: task.state.value.pending,
    },
    value => {
      const selected = value.filter(permission => !record.value.permissions.includes(permission))[0];
      trackEvent(EventTypes.roleAdd, EVENTS.PERMISSION_SELECTED, { value: selected });
      record.value.permissions = value;
    }),

    checkboxField({
      name: 'entityItemRestricted',
      label: t('role.enable-entity-item-restrictions'),
      value: record.value.entityItemRestricted,
      required: false,
      errors: validationErrors.value,
      disabled: task.state.value.pending,
      tooltipText: t('role.entity-item-restriction-tooltip'),
    }, value => {
      record.value.entityItemRestricted = value;
    }, null, [
      newFeature({ featureName: FeatureNames.ENABLE_ENTITY_ITEM_RESTRICTIONS }, 'newFeature'),
    ]),

    chooseUserFieldWithGroups({
      props: {
        value: record.value.users,
        options: userOptions,
        errors: validationErrors.value,
        disabled: task.state.value.pending,
        required: false,
        placeholder: t('role.to-user'),
        identifier: CreateUserModelIdentifier.roleCreator,
      },
      on: {
        add: (value: number[]) => {
          record.value.users = <number[]>onlyUnique([...record.value.users, ...value]);
        },
        input: (value: number[]) => {
          record.value.users = value;
        },
      },
    }),

    ssoConfiguration?.value.scimRoleMappingEnabled && inputField({
      name: 'scim-role-identifier',
      label: t('role.scim-role-identifier'),
      placeholder: t('role.scim-role-identifier'),
      value: record.value.scimRoleIdentifier || '',
      required: false,
      errors: validationErrors.value,
      disabled: task.state.value.pending,
    },
    value => {
      record.value.scimRoleIdentifier = value;
    }),

    apiErrorMessage(task.state.value, {
      'cannot.assign.role.to.all.admins': t('error.cant-assign-role-to-all-admins'),
    }),
  ];

  return {
    formFields,
    handleSubmit,
    isLoading,
  };
}
